import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Badge, Card, Empty, SectionTitle } from "./_ui";
import { STATUS_LABEL, customerWhatsAppHref } from "../../lib/adminUtils";
import { ArrowRightCircle, Check, Loader2, MessageCircle, RefreshCw } from "lucide-react";

export default function DashboardView({ staff, openCustomer, switchTab, reloadGlobal }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/dashboard");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading || !data) {
    return (
      <p className="text-sm text-slate-500">
        <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
      </p>
    );
  }

  const convertInquiry = async (id) => {
    const r = await api.post(`/admin/inquiries/${id}/convert`);
    await reloadGlobal?.();
    if (r.data?.customerId) openCustomer(r.data.customerId);
  };

  const markPaid = async (pl) => {
    await api.patch(`/admin/payment-links/${pl.id}`, { status: "manual_paid" });
    load();
    reloadGlobal?.();
  };

  const c = data.counts;

  return (
    <div className="space-y-8" data-testid="dashboard-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card title="Demandes non traitées" value={c.openInquiries} accent testid="card-inq" />
        <Card title="Paiements en attente" value={c.pendingPayments} testid="card-pay" />
        <Card title="Clients actifs" value={c.activeCustomers || 0} testid="card-active-customers" />
        <Card title="Personnel actif" value={staff.filter((s) => s.active).length} testid="card-staff" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
          <div>
            <SectionTitle
              action={
                <button onClick={() => switchTab("inquiries")} className="text-xs text-[#016C36] font-semibold hover:underline">
                  Voir tout
                </button>
              }
            >
              Demandes non traitées
            </SectionTitle>
            {data.openInquiries.length === 0 ? (
              <Empty label="Tout est traité." />
            ) : (
              <div className="space-y-2" data-testid="open-inq">
                {data.openInquiries.map((i) => (
                  <div key={i.id} className="bg-white border border-slate-200 rounded p-3 text-sm">
                    <div className="flex justify-between gap-2">
                      <div>
                        <p className="font-medium">{i.establishment}</p>
                        <p className="text-xs text-slate-500">{i.contactName} · {i.city}</p>
                      </div>
                      <Badge status={i.status} />
                    </div>
                    <div className="flex gap-1.5 mt-2">
                      <a
                        href={customerWhatsAppHref(i, `Bonjour ${i.contactName}, c'est SAFEMAT.`)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#25D366] text-white rounded"
                      >
                        <MessageCircle size={11} /> WhatsApp
                      </a>
                      <button
                        onClick={() => convertInquiry(i.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-200 rounded hover:bg-slate-50"
                        data-testid={`inq-convert-${i.id}`}
                      >
                        <ArrowRightCircle size={11} /> Convertir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <SectionTitle
              action={
                <button onClick={() => switchTab("payments")} className="text-xs text-[#016C36] font-semibold hover:underline">
                  Voir tout
                </button>
              }
            >
              Paiements en attente
            </SectionTitle>
            {data.pendingPayments.length === 0 ? (
              <Empty label="Aucun lien en attente." />
            ) : (
              <div className="space-y-2" data-testid="pending-pay">
                {data.pendingPayments.map((p) => (
                  <div key={p.id} className="bg-white border border-slate-200 rounded p-3 text-sm">
                    <p className="font-medium truncate">{p.description}</p>
                    <p className="text-xs text-slate-500">
                      CHF {Number(p.amount).toLocaleString("de-CH")} · {STATUS_LABEL[p.status] || p.status}
                    </p>
                    <div className="flex gap-1.5 mt-2">
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(`Bonjour,\n\nVoici votre lien de paiement SafeMat :\n\n${p.checkoutUrl || p.url}\n\nMerci pour votre confiance.`)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#25D366] text-white rounded"
                      >
                        <MessageCircle size={11} /> Relancer
                      </a>
                      <button
                        onClick={() => markPaid(p)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-emerald-300 text-emerald-700 rounded"
                        data-testid={`pay-paid-${p.id}`}
                      >
                        <Check size={11} /> Payé
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <SectionTitle
            action={
              <button onClick={load} className="btn-secondary !text-xs">
                <RefreshCw size={12} /> Rafraîchir
              </button>
            }
          >
            Personnel
          </SectionTitle>
          {staff.length === 0 ? (
            <Empty label="Ajoutez du personnel dans Paramètres." />
          ) : (
            <div className="space-y-2" data-testid="staff-list">
              {staff.filter((s) => s.active).map((s) => (
                <div key={s.id} className="bg-white border border-slate-200 rounded p-3 text-sm">
                  <p className="font-medium">{s.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.region || "Région non renseignée"}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
